/**
 * Shared State Management Hooks
 * Centralized hooks for common state management patterns
 * Maintains existing functionality while providing reusable patterns
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { debounce, throttle } from '../utils/sharedCore';
import { getStorageEngine } from '../app/di/container';
import { KV_COLLECTION, SESSION_COLLECTION } from '../infrastructure/storage/StorageEngine';

/**
 * Loading state hook
 * Manages loading states with async function wrapper
 */
export const useLoadingState = (initialState = false) => {
  const [isLoading, setIsLoading] = useState(initialState);
  
  const withLoading = useCallback(async (asyncFunction) => {
    setIsLoading(true);
    try {
      const result = await asyncFunction();
      return { success: true, data: result };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  return { isLoading, withLoading, setIsLoading };
};

/**
 * Data persistence hook — Storage Engine (ADR-0002), Collection `kv`.
 */
export const useDataPersistence = (key, defaultValue) => {
  const [data, setData] = useState(() => {
    try {
      const engine = getStorageEngine();
      const stored = engine.get(KV_COLLECTION, key);
      return stored !== undefined ? stored : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const updateData = useCallback((newData) => {
    setData(newData);
    const engine = getStorageEngine();
    engine.put(KV_COLLECTION, key, newData);
  }, [key]);
  
  return [data, updateData];
};

/**
 * Debounced state hook
 * State that updates with debouncing
 */
export const useDebouncedState = (initialValue, delay = 500) => {
  const [value, setValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return [value, setValue, debouncedValue];
};

/**
 * Throttled state hook
 * State that updates with throttling
 */
export const useThrottledState = (initialValue, delay = 100) => {
  const [value, setValue] = useState(initialValue);
  const [throttledValue, setThrottledValue] = useState(initialValue);
  const lastUpdate = useRef(0);
  
  const updateValue = useCallback((newValue) => {
    setValue(newValue);
    
    const now = Date.now();
    if (now - lastUpdate.current >= delay) {
      setThrottledValue(newValue);
      lastUpdate.current = now;
    }
  }, [delay]);
  
  return [value, updateValue, throttledValue];
};

/**
 * Previous value hook
 * Tracks previous value of state
 */
export const usePrevious = (value) => {
  const ref = useRef();
  
  useEffect(() => {
    ref.current = value;
  });
  
  return ref.current;
};

/**
 * Toggle state hook
 * Boolean state with toggle function
 */
export const useToggle = (initialValue = false) => {
  const [value, setValue] = useState(initialValue);
  
  const toggle = useCallback(() => setValue(prev => !prev), []);
  const setTrue = useCallback(() => setValue(true), []);
  const setFalse = useCallback(() => setValue(false), []);
  
  return [value, { toggle, setTrue, setFalse, setValue }];
};

/**
 * Counter hook
 * Number state with increment/decrement functions
 */
export const useCounter = (initialValue = 0, step = 1) => {
  const [count, setCount] = useState(initialValue);
  
  const increment = useCallback(() => setCount(prev => prev + step), [step]);
  const decrement = useCallback(() => setCount(prev => prev - step), [step]);
  const reset = useCallback(() => setCount(initialValue), [initialValue]);
  const setValue = useCallback((value) => setCount(value), []);
  
  return [count, { increment, decrement, reset, setValue }];
};

/**
 * Persisted key-value hook — Storage Engine, Collection `kv`.
 */
export const useLocalStorage = (key, defaultValue, validator = null) => {
  const [data, setData] = useState(() => {
    try {
      const engine = getStorageEngine();
      const stored = engine.get(KV_COLLECTION, key);
      if (stored !== undefined) {
        if (!validator || validator(stored)) {
          return stored;
        }
      }
    } catch (error) {
      console.warn(`Error reading storage key "${key}":`, error);
    }
    return defaultValue;
  });

  const setValue = useCallback(
    (newValue) => {
      try {
        if (validator && !validator(newValue)) {
          console.warn(`Validation failed for storage key "${key}"`);
          return;
        }

        setData(newValue);
        const engine = getStorageEngine();
        engine.put(KV_COLLECTION, key, newValue);
      } catch (error) {
        console.error(`Error setting storage key "${key}":`, error);
      }
    },
    [key, validator]
  );

  const removeValue = useCallback(() => {
    setData(defaultValue);
    const engine = getStorageEngine();
    engine.delete(KV_COLLECTION, key);
  }, [key, defaultValue]);
  
  return [data, setValue, removeValue];
};

/**
 * Session-scoped persistence — Storage Engine, Collection `session` (browser sessionStorage).
 */
export const useSessionStorage = (key, defaultValue) => {
  const [data, setData] = useState(() => {
    try {
      const engine = getStorageEngine();
      const stored = engine.get(SESSION_COLLECTION, key);
      return stored !== undefined ? stored : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const updateData = useCallback((newData) => {
    setData(newData);
    const engine = getStorageEngine();
    engine.put(SESSION_COLLECTION, key, newData);
  }, [key]);
  
  return [data, updateData];
};

/**
 * Async state hook
 * Manages async operations with loading, error, and data states
 */
export const useAsyncState = (initialData = null) => {
  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const execute = useCallback(async (asyncFunction) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await asyncFunction();
      setData(result);
      return { success: true, data: result };
    } catch (err) {
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setIsLoading(false);
  }, [initialData]);
  
  return {
    data,
    isLoading,
    error,
    execute,
    reset,
    setData,
    setError
  };
};

/**
 * Form state hook
 * Manages form state with validation
 */
export const useFormState = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  const setValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);
  
  const setTouchedField = useCallback((name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);
  
  const validateField = useCallback((name, value) => {
    const rules = validationRules[name];
    if (!rules) return null;
    
    for (const rule of rules) {
      const error = rule(value);
      if (error) return error;
    }
    return null;
  }, [validationRules]);
  
  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;
    
    Object.keys(validationRules).forEach(name => {
      const error = validateField(name, values[name]);
      if (error) {
        newErrors[name] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  }, [values, validateField, validationRules]);
  
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);
  
  return {
    values,
    errors,
    touched,
    setValue,
    setTouchedField,
    validateField,
    validateForm,
    reset,
    setValues,
    setErrors,
    setTouched
  };
};
